import { Button } from '@components/_ui/Button'
import { Checkbox } from '@components/_ui/Checkbox'
import { Counter } from '@components/_ui/Counter'
import { ExplanationText } from '@components/_ui/ExplanationText'
import { Grid } from '@components/_ui/Grid'
import { Loader } from '@components/_ui/Loader'
import { MotionWrapper } from '@components/_ui/MotionWrapper'
import { Question } from '@components/_ui/Question'
import { TooltipInfo } from '@components/_ui/TooltipInfo'
import { useFetch } from '@hooks/useFetch'
import { usePriceSetter } from '@hooks/usePriceSetter'
import { useScrollToNewQuestion } from '@hooks/useScrollToNewQuestion'
import { useSteps } from '@hooks/useSteps'
import { transformFormData } from '@lib/transformFormData'
import { unregisterUnusedFields } from '@lib/unregisterUnusedFields'
import { QuestionType } from '@types'
import clsx from 'clsx'
import { AnimatePresence } from 'motion/react'
import { Fragment, ReactNode, useEffect, useMemo } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'

import s from './Form.module.scss'
import type { FormProps } from './Form.props'

export function Form({ data }: FormProps) {
  const { register, setValue, unregister } = useFormContext()

  const isCarpetCleaning = useWatch({ name: 'is_carpet_cleaning' })
  const carpetZonesNum = useWatch({ name: 'carpet_zones_num' })
  const isUpholstery = useWatch({ name: 'is_upholstery' })
  const deepCleaned = useWatch({ name: 'deep_cleaned' })

  const questions = useMemo(() =>
    data
      .filter((question) => {
        if (
          (['carpet_zones_num', 'is_carpeted_stairs'].includes(question.db_field_name) && (!isCarpetCleaning || isCarpetCleaning === 'false'))
          || (question.db_field_name === 'deep_cleaned' && (!isUpholstery || isUpholstery === 'false'))
        ) {
          return false
        } else {
          return true
        }
      })
  , [isCarpetCleaning, isUpholstery])

  const questionRefs = useScrollToNewQuestion(questions)

  useEffect(() => {
    unregisterUnusedFields({ initialData: data, usedData: questions, unregister })
  }, [questions])

  return (
    <div className={s.form}>
      <AnimatePresence initial={false}>
        {questions?.length && questions.map((question, index) => (
          <MotionWrapper
            key={question.db_field_name}
            ref={(el) => (questionRefs.current[question.db_field_name] = el)}
            className={clsx({ [s.questionWrapper]: ['carpet_zones_num', 'is_carpeted_stairs', 'deep_cleaned'].includes(question.db_field_name) })}
          >
            {!!question?.question && (
              <Question
                question={question.question}
                info={question?.explanation?.content}
                index={index + 1}
              />
            )}

            <ExplanationText data={question.explanation} />

            {({
              single: (
                <Grid
                  options={question.options}
                  {...register(question.db_field_name)}
                />
              ),
              multiply: question.db_field_name === 'is_carpeted_stairs'
                ? question?.options?.map((option, index) => (
                  <Fragment key={index}>
                    <hr className={s.hr} />
                    <Checkbox
                      {...register(question.db_field_name)}
                      value={option.db_item_name}
                      label={
                        <span className={s.checkboxWrapper}>
                          {option.item}
                          <span onClick={(ev) => ev.preventDefault()}>
                            <TooltipInfo text={<div dangerouslySetInnerHTML={{ __html: question?.explanation?.content || '' }} />} />
                          </span>
                        </span>
                      }
                    />
                  </Fragment>
                ))
                : (
                  <Grid
                    options={question.options}
                    {...register(question.db_field_name)}
                    type="checkbox"
                  />
                ),
              numeric: question?.options?.map((option, index) => (
                <Counter
                  key={index}
                  min={Number(option?.min)}
                  max={Number(option?.max)}
                  initValue={{
                    deep_cleaned: deepCleaned?.[index] ?? Number(option?.min),
                    carpet_zones_num: carpetZonesNum ?? Number(option?.min)
                  }[question.db_field_name] ?? Number(option?.min)}
                  icon={option?.image}
                  onChange={(value) => setValue(
                    question.db_field_name === 'deep_cleaned'
                      ? `${question.db_field_name}.${index}`
                      : question.db_field_name
                    , value
                  )}
                  text={option?.item}
                  type={question.db_field_name === 'deep_cleaned' ? 'reversed' : 'center'}
                />
              ))
            } as Record<QuestionType, ReactNode | ReactNode[]>)[question.question_type]}
          </MotionWrapper>
        ))}
      </AnimatePresence>
    </div>
  )
}

function FormButton({ data }: FormProps) {
  const { handleSubmit } = useFormContext()
  const { trigger, isLoading } = useFetch()
  const { stepsHistory, setStepsHistory, setCurrentStep } = useSteps()
  const setPriceData = usePriceSetter()

  const isCarpetCleaning = useWatch({ name: 'is_carpet_cleaning' })
  const isUpholstery = useWatch({ name: 'is_upholstery' })
  const deepCleaned = useWatch({ name: 'deep_cleaned' })

  const sum = deepCleaned?.reduce((prev: number, curr: number) => prev + curr, 0)
  const disabled = !isCarpetCleaning || !isUpholstery || (isUpholstery === 'true' && sum < 1)

  const nextHandler = handleSubmit((formData) => {
    if (disabled) return

    const nextStep = 4
    const newHistory = [...stepsHistory, 4]

    const transformedFormData = transformFormData(data, formData)

    trigger({ ...transformedFormData, current_screen: nextStep, history: newHistory })
      .then((res) => {
        setPriceData((prev) => ({ ...prev, ...res?.data?.data }))
        setCurrentStep(nextStep)
        setStepsHistory(newHistory)
      })
  })

  return (
    <Button
      onClick={nextHandler}
      disabled={isLoading || disabled}
    >
      {isLoading ? <Loader /> : 'Next'}
    </Button>
  )
}

Form.Button = FormButton
